import { Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';

import { Update } from '@ngrx/entity';
import { BehaviorSubject } from 'rxjs';
import { take } from 'rxjs/operators';

import { NotificationsEntity } from '../models/notifications';
import * as NotificationsActions from './notifications.actions';
import * as NotificationsSelectors from './notifications.selectors';

@Injectable()
export class NotificationsFacade {
    /**
     * Combine pieces of state using createSelector,
     * and expose them as observables through the facade.
     */
    loaded$ = this.store.pipe(
        select(NotificationsSelectors.getNotificationsLoaded)
    );
    allNotifications$ = this.store.pipe(
        select(NotificationsSelectors.getAllNotifications)
    );
    selectedNotification$ = this.store.pipe(
        select(NotificationsSelectors.getSelected)
    );
    unreadCount$ = this.store.pipe(
        select(NotificationsSelectors.getUnreadCount)
    );
    browserNotification$: BehaviorSubject<NotificationsEntity | null> =
        new BehaviorSubject<NotificationsEntity | null>(null);

    canLoadMoreNotifications$ = this.store.pipe(
        select(NotificationsSelectors.canLoadPage)
    );
    paginationInfo$ = this.store.pipe(
        select(NotificationsSelectors.getPaginationInfo)
    );
    isLoadingPage$ = this.store.pipe(
        select(NotificationsSelectors.getIsLoadingPage)
    );

    constructor(private readonly store: Store) {}

    /**
     * Use the initialization action to load the state
     * (e.g. from localStorage or remote server)
     */
    init() {
        this.store.dispatch(NotificationsActions.init());
    }

    /**
     * Loads the notifications
     */
    loadNotifications() {
        this.store.dispatch(NotificationsActions.loadNotifications());
    }

    /**
     * Loads the unread notifications count.
     */
    loadUnreadNotifications() {
        this.store.dispatch(NotificationsActions.loadUnreadNotifications());
    }

    /**
     * Marks a notification as read
     * @param notification
     */
    markAsRead(notification: NotificationsEntity) {
        this.store.dispatch(
            NotificationsActions.markNotificationAsRead({ notification })
        );
    }

    /**
     * Marks all notifications as read
     */
    markAllAsRead() {
        this.store.dispatch(NotificationsActions.markAllNotificationsAsRead());
    }

    /**
     * Toggles the selection state of a notification.
     * @param notification The notification to toggle.
     */
    toggleNotificationSelected(notification: NotificationsEntity) {
        this.store
            .pipe(
                select(NotificationsSelectors.getSelectedId),
                take(1)
            )
            .subscribe(selectedId => {
                if (selectedId?.toString() === notification.id) {
                    this.store.dispatch(NotificationsActions.unselectNotification());
                } else {
                    this.store.dispatch(NotificationsActions.selectNotification({ notification }));
                }
            });
    }

    /**
     * Deletes a notification.
     * @param notification The notification to delete.
     */
    deleteNotification(notification: NotificationsEntity) {
        this.store.dispatch(NotificationsActions.deleteNotification({ notification }));
    }

    /**
     * Toggles the read status of a notification.
     * @param notification The notification to toggle.
     */
    toggleNotificationRead(notification: NotificationsEntity) {
        const update: Update<NotificationsEntity> = {
            id: notification.id,
            changes: { read: !notification.read },
        };
        this.store.dispatch(NotificationsActions.toggleNotificationRead({ notification: update }));
    }

    /**
     * Loads more notifications.
     */
    loadMoreNotifications() {
        this.store.dispatch(NotificationsActions.loadMoreNotifications());
    }

    /**
     * Initializes the Echo listener for real-time notifications.
     * @param token The authentication token for Echo.
     * @param userId The ID of the current user.
     */
    initializeEchoListener(token: string, userId: string) {
        this.store.dispatch(NotificationsActions.initializeEchoListener({ token, userId }));
    }

    /**
     * Deinitializes the Echo listener.
     */
    deinitializeEchoListener() {
        this.store.dispatch(NotificationsActions.deinitializeEchoListener());
    }

    /**
     * Reinitializes the Echo listener with the provided token and user ID.
     * This is useful when the connection state changes or the token needs to be refreshed.
     * @param token The authentication token for Echo.
     * @param userId The ID of the current user.
     */
    reinitializeEchoListener(token: string, userId: string) {
        this.store.dispatch(NotificationsActions.initializeEchoListener({ token, userId }));
    }
}
